import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";

// Anything past this in a cell is hidden behind a hover, so one huge LLM_Raw_Response
// cannot stretch a column past the width of the screen.
const CELL_CLAMP = 90;

const COL_HEADER_SX = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: SLATE[600],
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  bgcolor: SLATE[50],
  borderBottom: `1px solid ${SLATE[300]}`,
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const CELL_SX = {
  fontSize: "0.8125rem",
  color: SLATE[800],
  borderBottom: `1px solid ${SLATE[100]}`,
  verticalAlign: "top",
  maxWidth: 420,
};

function Cell({ value, header }) {
  const text = value == null ? "" : String(value);
  const clamped = text.length > CELL_CLAMP;
  const shown = clamped ? `${text.slice(0, CELL_CLAMP)}…` : text;

  if (header === "Match") {
    return (
      <TableCell sx={CELL_SX}>
        <Chip
          label={text || "—"}
          size="small"
          color={text === "Yes" ? "success" : "default"}
          variant={text === "Yes" ? "filled" : "outlined"}
          sx={{ fontSize: "0.6875rem", height: 20 }}
        />
      </TableCell>
    );
  }

  return (
    <TableCell sx={CELL_SX}>
      {clamped ? (
        <Tooltip title={text}>
          <span style={{ cursor: "help" }}>{shown}</span>
        </Tooltip>
      ) : (
        shown || "—"
      )}
    </TableCell>
  );
}

/**
 * Shows a finished run's comparison sheet as a table. The rows come from the API rather
 * than a browser fetch of the presigned S3 url, which would need CORS on the bucket;
 * the download link below points straight at S3, which does not.
 */
export default function ResultSheetDialog({ open, onClose, run, tokens }) {
  const { makeRequest } = useApiCall();

  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !run?.run_id) return undefined;

    let cancelled = false;
    setSheet(null);
    setError(null);
    setLoading(true);

    makeRequest(`/v1/intent_model_test_runs/${encodeURIComponent(run.run_id)}/results`, {
      method: "GET",
      authTokens: tokens,
    })
      .then((response) => {
        if (!cancelled) setSheet(response);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load the result sheet.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, run?.run_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = sheet?.headers || [];
  const rows = sheet?.rows || [];

  const matched = useMemo(() => {
    const idx = headers.indexOf("Match");
    if (idx === -1) return null;
    return rows.filter((row) => row[idx] === "Yes").length;
  }, [headers, rows]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: "94vh", maxHeight: "94vh", width: "97vw", maxWidth: "1800px" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
              Result sheet
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: SLATE[500],
                fontFamily: "ui-monospace, Menlo, monospace",
                wordBreak: "break-all",
              }}
            >
              {run?.result_file_name || run?.run_id}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            {rows.length > 0 && (
              <>
                <Chip
                  label={`${rows.length} rows`}
                  size="small"
                  sx={{ fontSize: "0.6875rem", height: 22 }}
                />
                {matched !== null && (
                  <Chip
                    label={`${matched} / ${rows.length} matched`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: "0.6875rem", height: 22 }}
                  />
                )}
              </>
            )}
            {run?.result_url && (
              <Button
                component="a"
                href={run.result_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: "1rem" }} />}
                sx={{ textTransform: "none", fontSize: "0.875rem", whiteSpace: "nowrap" }}
              >
                Download CSV
              </Button>
            )}
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <CloseIcon sx={{ fontSize: "1.1rem", color: SLATE[500] }} />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {error && (
          <Alert severity="error" sx={{ m: 2, fontSize: "0.8125rem" }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && !error && rows.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[600] }}>
              This sheet has no rows.
            </Typography>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ overflow: "auto", flex: 1, px: 2, pb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell key={header} sx={COL_HEADER_SX}>
                      {header.replace(/_/g, " ")}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={rowIndex} hover>
                    {headers.map((header, colIndex) => (
                      <Cell key={header} header={header} value={row[colIndex]} />
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontSize: "0.875rem", color: SLATE[600] }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
